import { Box, Button, styled, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import leftArrow from "../../assets/images/left-arrow.png";
import colors from "../../theme/colors";
import fonts from "../../theme/fonts";
import { KEYBOARD_KEY } from "../../types/keyboardKeys";
import { PLAYER } from "../../types/players";

interface NumpadProps {
  scoreHandler: Function;
  undoHandler: Function;
  currentPlayer: PLAYER;
  disabled: boolean;
  inUndoState: boolean;
}

const STARTING_SCORE = "0";

export default function Numpad({
  scoreHandler,
  undoHandler,
  currentPlayer,
  inUndoState,
  disabled = false,
}: NumpadProps) {
  const [roundScore, setRoundScore] = useState(STARTING_SCORE);

  useEffect(() => {
    function handleKeyUp({ key }: any) {
      if (!isNaN(parseInt(key))) {
        onNumberInput(parseInt(key));
        return;
      }
      if (key === KEYBOARD_KEY.BACKSPACE) {
        eraseNumber();
        return;
      }
      if (key === KEYBOARD_KEY.ENTER) {
        onEnterInput();
      }
    }
    document.addEventListener("keyup", handleKeyUp);

    // Don't forget to clean up
    return function cleanup() {
      document.removeEventListener("keyup", handleKeyUp);
    };
    // eslint-disable-next-line
  }, [roundScore]);

  const onNumberInput = (number: number) => {
    if (roundScore === STARTING_SCORE) {
      setRoundScore(`${number}`);
      return;
    }

    const newScore = `${roundScore}${number}`;
    if (parseInt(newScore) > 180) return;
    setRoundScore(newScore);
  };

  const numberButtons = (): JSX.Element[] => {
    const buttons: JSX.Element[] = [];
    for (let i = 9; i > 0; i--) {
      buttons.push(
        <ButtonWrapper key={`numpad_${i}`}>
          <NumberButton
            disabled={disabled}
            player={currentPlayer}
            variant="contained"
            onClick={() => onNumberInput(i)}
          >
            {i}
          </NumberButton>
        </ButtonWrapper>
      );
    }
    return buttons;
  };

  const eraseNumber = () => {
    if (roundScore === STARTING_SCORE) return;
    if (roundScore.length === 1) {
      setRoundScore(STARTING_SCORE);
      return;
    }
    setRoundScore((score) => score.substring(0, score.length - 1));
  };

  const onEnterInput = () => {
    const score = parseInt(roundScore);

    if (isNaN(score) || score > 180) return;
    scoreHandler(score);
    setRoundScore(STARTING_SCORE);
  };

  return (
    <Wrapper>
      <ScoreWrapper>
        <Score>{roundScore}</Score>
      </ScoreWrapper>
      <InputWrapper>
        {numberButtons()}
        <ButtonWrapper>
          <BackspaceEnterButton
            disabled={disabled}
            player={currentPlayer}
            variant="contained"
            onClick={() => eraseNumber()}
          >
            <img
              style={{ height: "35px", opacity: disabled ? ".1" : "1" }}
              src={leftArrow}
              alt="erase"
            ></img>
          </BackspaceEnterButton>
        </ButtonWrapper>
        <ButtonWrapper>
          <NumberButton
            disabled={disabled}
            player={currentPlayer}
            variant="contained"
            onClick={() => onNumberInput(0)}
          >
            0
          </NumberButton>
        </ButtonWrapper>
        <ButtonWrapper>
          <BackspaceEnterButton
            disabled={disabled}
            player={currentPlayer}
            variant="contained"
            onClick={() => onEnterInput()}
          >
            Enter
          </BackspaceEnterButton>
        </ButtonWrapper>
        <UndoButtonWrapper>
          <UndoButton
            disabled={disabled && !inUndoState}
            variant="contained"
            onClick={() => undoHandler()}
          >
            Undo
          </UndoButton>
        </UndoButtonWrapper>
      </InputWrapper>
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "10%",
  width: "70%",
  height: "100%",
  "@media(max-width: 600px)": {
    width: "90%",
  },
});

const ScoreWrapper = styled(Box)({});

const Score = styled(Typography)({
  fontSize: fonts.size.extraLarge,
  fontFamily: fonts.family.chalk,
  color: colors.lightBeige,
});

const InputWrapper = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  width: "100%",
  height: "75%",
  gap: "10px",
});

const ButtonWrapper = styled(Box)({
  width: "100%",
  height: "100%",
});

interface ButtonProps {
  player?: PLAYER;
}

const BaseButton = styled(Button)<ButtonProps>({
  height: "100%",
  width: "100%",
  fontSize: fonts.size.large,
  textTransform: "none",
});

const NumberButton = styled(BaseButton)(({ player }) => ({
  backgroundColor:
    player === PLAYER.FIRST ? colors.lightBeige : colors.darkGray,
  color: player === PLAYER.FIRST ? colors.darkGray : colors.lightBeige,
  "&:hover": {
    backgroundColor:
      player === PLAYER.FIRST ? colors.darkBeige : colors.lightGray,
    color: colors.darkGray,
  },
}));

const BackspaceEnterButton = styled(BaseButton)(({ player }) => ({
  backgroundColor: player === PLAYER.FIRST ? colors.darkGreen : colors.darkRed,
  "&:hover": {
    backgroundColor:
      player === PLAYER.FIRST ? colors.lightGreen : colors.lightRed,
  },
}));

const UndoButtonWrapper = styled(Box)({
  gridColumn: "span 3",
  width: "100%",
  height: "80%",
});

const UndoButton = styled(BaseButton)({
  backgroundColor: "#92b19c",
  color: colors.darkGray,
  "&:hover": {
    backgroundColor: "#C7AB8D",
  },
});
